import React, { useState } from "react";
import { Text, LoadingOverlay, Stack, Center } from "@mantine/core";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  SearchInput,
  Button,
  Tag,
  TooltipProvider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSelectTrigger,
  Checkbox,
  DatePicker,
} from "@jem-open/jem-ui";
import {
  MapPinOff,
  Users,
  Clock,
  Settings,
  Download,
  Bell,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  STATUS_FILTER_DESCRIPTORS,
  StatusFilterValue,
} from "../utils/statusFilterConfig";
import { computeAttendanceCounts } from "../utils/attendanceUtils";
import PageContainer from "../../../themes/components/PageContainer";
import useShiftSiteAttendance from "../hooks/useShiftSiteAttendance";
import EmployeeTimelineRow from "../components/EmployeeTimelineRow";
import AccessGuard from "../../../common/uac/AccessGuard";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import ReportWizardDialog from "../../reports/components/ReportWizardDialog";
import { ReportScope } from "../../reports/services/model/ReportScope";
import { useExceptionCountApiService } from "../services/api/AttendanceExceptionsApiService";

const ShiftSiteAttendancePageInner: React.FC = () => {
  const { shiftSiteId } = useParams<{ shiftSiteId: string }>();
  const navigate = useNavigate();

  const {
    site,
    filteredShiftLocations,
    availableRosters,
    shiftConfigs,
    rosterFilter,
    setRosterFilter,
    shiftFilter,
    setShiftFilter,
    statusFilters,
    setStatusFilters,
    searchQuery,
    setSearchQuery,
    date,
    setDate,
    isLoading,
    error,
    stats,
  } = useShiftSiteAttendance(shiftSiteId || "");

  const [statusMenuOpened, setStatusMenuOpened] = useState(false);
  const [pendingFilters, setPendingFilters] = useState<StatusFilterValue[]>([]);
  const [reportDialogScope, setReportDialogScope] =
    useState<ReportScope | null>(null);
  const reportsAgentUiEnabled = useEnabledFeatureFlag(
    FeatureFlags.REPORTS_AGENT_UI
  );
  // Use the route param (available on first render) rather than site?.id (only
  // set once the attendance query resolves) so the count is scoped immediately
  // and never fires an unfiltered, employer-wide request.
  const siteExceptionCountQuery = useExceptionCountApiService({
    shift_site: shiftSiteId,
    status: "open",
  });
  const unresolvedSiteExceptionCount = siteExceptionCountQuery.data?.count ?? 0;
  const isTodayView = dayjs(date).isSame(dayjs(), "day");
  const isYesterdayView = dayjs(date).isSame(dayjs().subtract(1, "day"), "day");
  let datePreset: "today" | "yesterday" | "custom" = "custom";
  if (isTodayView) datePreset = "today";
  else if (isYesterdayView) datePreset = "yesterday";

  const handleDatePresetChange = (val: string): void => {
    if (val === "today") {
      setDate(dayjs().format("YYYY-MM-DD"));
    } else if (val === "yesterday") {
      setDate(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
    } else if (val === "custom" && datePreset !== "custom") {
      // Default custom selection to two days ago so the picker has a sensible
      // starting value the user can then refine.
      setDate(dayjs().subtract(2, "day").format("YYYY-MM-DD"));
    }
  };
  if (!shiftSiteId) return null;

  const renderContent = (): JSX.Element | null => {
    if (isLoading) {
      return <LoadingOverlay visible />;
    }

    if (error) {
      return (
        <Center style={{ height: 300 }}>
          <Stack align="center" spacing="md">
            <MapPinOff size={48} color="#9CA3AF" />
            <Text color="dimmed">Failed to load site data.</Text>
          </Stack>
        </Center>
      );
    }

    if (filteredShiftLocations.length === 0) {
      return (
        <Center style={{ height: 200 }}>
          <Stack align="center" spacing="sm">
            <Text color="dimmed">
              No matching records for the selected filters.
            </Text>
          </Stack>
        </Center>
      );
    }

    // Default: first accordion expanded
    const defaultValues =
      filteredShiftLocations.length > 0
        ? [filteredShiftLocations[0].location.id]
        : [];

    return (
      <Accordion type="multiple" defaultValue={defaultValues}>
        {filteredShiftLocations.map((group) => {
          const loc = group.location;
          const lateThreshold = loc.late_arrival_threshold_minutes ?? 5;
          const counts = computeAttendanceCounts(group.records);
          const coverage =
            counts.scheduled > 0
              ? Math.min(
                  100,
                  Math.round((counts.covered / counts.scheduled) * 100)
                )
              : null;

          return (
            <AccordionItem key={loc.id} value={loc.id}>
              <div className="flex items-center gap-2 py-3">
                <AccordionTrigger className="py-0 px-0 flex-1">
                  <span className="text-base font-bold text-[#384d5c]">
                    {loc.name} ({counts.covered}/{counts.scheduled})
                    {coverage !== null ? ` ${coverage}%` : ""}
                  </span>
                </AccordionTrigger>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-gray-100 text-[#829099] hover:text-[#062133] transition-colors"
                  title="Configure location"
                  aria-label={`Configure ${loc.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/site-configuration/${shiftSiteId}`);
                  }}
                >
                  <Settings size={16} />
                </button>
              </div>
              <AccordionContent>
                {reportsAgentUiEnabled && site && (
                  <div className="flex justify-end mb-2">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Download size={16} />}
                      onClick={() =>
                        setReportDialogScope({
                          kind: "roster",
                          id: loc.id,
                          label: `${site.name}: ${loc.name}`,
                          parentSite: site.name,
                        })
                      }
                    >
                      Download Report
                    </Button>
                  </div>
                )}
                <div className="space-y-2">
                  {group.records.map((record) => (
                    <EmployeeTimelineRow
                      key={`${record.employee_id}-${record.shift_start}-${record.shift_end}`}
                      employee={record}
                      shiftStart={record.shift_start}
                      shiftEnd={record.shift_end}
                      lateThreshold={lateThreshold}
                      locationId={loc.id}
                      locationLatitude={loc.latitude}
                      locationLongitude={loc.longitude}
                      locationRadius={loc.radius_allowance}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <PageContainer>
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/time-attendance");
                }}
              >
                Time & Attendance
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            {site?.site_group_name && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/time-attendance");
                    }}
                  >
                    {site.site_group_name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            )}
            <BreadcrumbItem>
              <BreadcrumbPage>{site?.name || "Loading..."}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="mb-4">
          <div className="flex flex-col gap-sm mb-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-medium text-[#062133] m-0">
                {site?.name || ""}
              </h2>
              {site?.client_name && (
                <Tag variant="destructive" className="text-xs">
                  {site.client_name}
                </Tag>
              )}
            </div>
            <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-2">
              {site && (
                <Button
                  variant="outline"
                  leftIcon={<Bell size={16} />}
                  onClick={() =>
                    navigate(
                      `/time-attendance/exceptions?shift_site=${
                        site.id
                      }&site_name=${encodeURIComponent(site.name)}`
                    )
                  }
                  className="w-full sm:w-auto"
                >
                  View Exceptions
                  <Tag
                    variant={
                      unresolvedSiteExceptionCount > 0 ? "failed" : "drafted"
                    }
                    size="sm"
                    className="ml-xxs min-w-[20px] justify-center"
                    aria-label={`${unresolvedSiteExceptionCount} unresolved exceptions`}
                  >
                    {unresolvedSiteExceptionCount}
                  </Tag>
                </Button>
              )}
              {reportsAgentUiEnabled && site && (
                <Button
                  variant="outline"
                  leftIcon={<Download size={16} />}
                  onClick={() =>
                    setReportDialogScope({
                      kind: "site",
                      id: site.id,
                      label: site.name,
                      region: site.site_group_name ?? undefined,
                    })
                  }
                  className="w-full sm:w-auto"
                >
                  Download Report
                </Button>
              )}
              <Button
                variant="primary"
                disabled={filteredShiftLocations.length === 0}
                onClick={() => {
                  const firstLoc = filteredShiftLocations[0]?.location?.id;
                  if (firstLoc) {
                    navigate(`/time-attendance/${firstLoc}/clock`);
                  }
                }}
                className="w-full sm:w-auto"
              >
                Clock In/Out Employees
              </Button>
            </div>
          </div>
        </div>

        {/* Date label */}
        <div className="mb-4">
          <p className="text-sm text-[#062133] m-0">
            Date:{" "}
            <span className="font-semibold">
              {dayjs(date).format("dddd, MMMM D, YYYY")}
            </span>
          </p>
        </div>

        <div
          className="mb-4"
          style={{
            height: 1,
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0.07) 100%)",
          }}
        />

        {/* Search */}
        <div className="mb-4">
          <SearchInput
            placeholder="Search employee, on site, tags etc."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            className="w-full sm:max-w-md"
          />
        </div>

        {/* Filters row */}
        <div className="flex flex-col gap-xs mb-4 sm:flex-row sm:items-center">
          <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-2">
            <Select value={datePreset} onValueChange={handleDatePresetChange}>
              <SelectTrigger className="w-full sm:w-[160px] border-[#cecece] rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="custom">Custom date</SelectItem>
              </SelectContent>
            </Select>
            {datePreset === "custom" && (
              <DatePicker
                date={dayjs(date).toDate()}
                onDateChange={(d) => {
                  if (d) setDate(dayjs(d).format("YYYY-MM-DD"));
                }}
                placeholder="Select date"
                className="w-full sm:w-[180px]"
              />
            )}
            <Select value={rosterFilter} onValueChange={setRosterFilter}>
              <SelectTrigger className="w-full sm:w-[160px] border-[#cecece] rounded-lg">
                <SelectValue placeholder="All Rosters" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rosters</SelectItem>
                {availableRosters.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={shiftFilter} onValueChange={setShiftFilter}>
              <SelectTrigger className="w-full sm:w-[160px] border-[#cecece] rounded-lg">
                <SelectValue placeholder="All Shifts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shifts</SelectItem>
                {shiftConfigs.map((config) => (
                  <SelectItem key={config.id} value={config.id}>
                    {config.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DropdownMenu
              open={statusMenuOpened}
              onOpenChange={(open) => {
                if (open) {
                  setPendingFilters(statusFilters);
                  setStatusMenuOpened(true);
                } else {
                  const sorted = [...pendingFilters].sort();
                  const current = [...statusFilters].sort();
                  if (JSON.stringify(sorted) !== JSON.stringify(current)) {
                    setStatusFilters(pendingFilters);
                  }
                  setStatusMenuOpened(false);
                }
              }}
            >
              <DropdownMenuSelectTrigger
                className="w-full sm:w-[160px] border-[#cecece] rounded-lg"
                placeholder="All"
              >
                {(() => {
                  const active = statusMenuOpened
                    ? pendingFilters
                    : statusFilters;
                  if (active.length === 0) return "All";
                  const labels = STATUS_FILTER_DESCRIPTORS.filter((o) =>
                    active.includes(o.value as StatusFilterValue)
                  ).map((o) => o.label);
                  if (labels.length === 1) return labels[0];
                  return `${labels[0]} +${labels.length - 1}`;
                })()}
              </DropdownMenuSelectTrigger>
              <DropdownMenuContent align="start" matchTriggerWidth>
                {STATUS_FILTER_DESCRIPTORS.map((option) => {
                  const isSelected = pendingFilters.includes(
                    option.value as StatusFilterValue
                  );
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      onSelect={(e) => {
                        e.preventDefault();
                        if (isSelected) {
                          setPendingFilters(
                            pendingFilters.filter((f) => f !== option.value)
                          );
                        } else {
                          setPendingFilters([
                            ...pendingFilters,
                            option.value as StatusFilterValue,
                          ]);
                        }
                      }}
                      className="gap-2 pl-2"
                    >
                      <Checkbox
                        checked={isSelected}
                        tabIndex={-1}
                        className="[&_svg]:text-white [&_svg]:stroke-white"
                      />
                      {option.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex flex-wrap items-center gap-x-md gap-y-xs mb-4 text-xs font-medium text-[#829099] sm:gap-6">
          <div className="flex items-center gap-1">
            <Users size={16} />
            <span>
              {stats.totalOnSite} of {stats.totalScheduled} Covered
              {isTodayView ? ` · ${stats.presentNow} on site now` : ""}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Clock size={16} />
            <span>
              Shift Coverage:{" "}
              {stats.coveragePercent !== null
                ? `${stats.coveragePercent}%`
                : "-"}
            </span>
          </div>
        </div>

        {/* Accordion content */}
        {renderContent()}

        {reportDialogScope && (
          <ReportWizardDialog
            open
            onOpenChange={(o) => !o && setReportDialogScope(null)}
            scope={reportDialogScope}
          />
        )}
      </PageContainer>
    </TooltipProvider>
  );
};

const ShiftSiteAttendancePage: React.FC = () => (
  <AccessGuard>
    <ShiftSiteAttendancePageInner />
  </AccessGuard>
);

export default ShiftSiteAttendancePage;
